/**
 * The catalogue's translated copy lives in shared, because a receipt names
 * its lines with it too and subscription may not import marketplace (which
 * imports subscription). Re-exported so the catalogue keeps one door.
 */
import type { ToolLike } from "../../../shared/i18n/toolCopy";

export { moduleName, toolDescription, toolName, toolShortDescription } from "../../../shared/i18n/toolCopy";

/**
 * Tools still finding their shape.
 *
 * Said in the catalogue rather than only in the description, because whether a
 * tool is finished is the first thing a therapist deciding to rely on one
 * needs to know — and the last thing they will read a paragraph to find out.
 * Kept here beside the copy overrides: it is a fact about our first-party
 * tools, not a column the catalogue carries.
 */
const BETA_SLUGS = new Set(["alchely-meeting", "emdr"]);

export const isBeta = (tool: ToolLike): boolean => BETA_SLUGS.has(tool.slug);

/**
 * Public-site slugs for a tool's marketing and documentation pages.
 *
 * alchely.com is a separate site with its own naming history, so its slugs do
 * not always match the catalogue's: the constellation tool is plural there,
 * and `ai-insights` — renamed to Augmented Memory in the app while keeping its
 * identity slug — is `clinical-insights` in the marketplace and
 * `augmented-memory` in the docs. Mapped explicitly rather than derived, so a
 * mismatch shows up here instead of as a 404 in a therapist's new tab.
 *
 * Every entry here was checked against the live site — except
 * `genogram-ecomap`, `meditation` and `emdr`, whose pages are not yet
 * published: those URLs answer 404 today (checked 2026-10-08 for `emdr`) and
 * have to be up before the tools are.
 */
const TOOL_SITE_SLUGS: Record<string, { marketplace: string; docs: string }> = {
    constellation: { marketplace: "constellations", docs: "constellation" },
    "ai-insights": { marketplace: "clinical-insights", docs: "augmented-memory" },
    scheduler: { marketplace: "scheduler", docs: "scheduler" },
    payments: { marketplace: "payments", docs: "payments" },
    "alchely-meeting": { marketplace: "alchely-meeting", docs: "alchely-meeting" },
    "genogram-ecomap": { marketplace: "genogram-ecomap", docs: "genogram-ecomap" },
    meditation: { marketplace: "meditation", docs: "meditation" },
    emdr: { marketplace: "emdr", docs: "emdr" },
};

export interface ToolExternalLinks {
    moreInfo: string;
    documentation: string;
}

/** The public site speaks the two languages the app does, and nothing else. */
const siteLanguage = (language: string | undefined): "es" | "en" =>
    language?.startsWith("es") ? "es" : "en";

/**
 * Links to the tool's page on the public site, or `null` for a tool that has
 * none — a third-party entry, or a first-party one whose pages aren't up yet.
 */
export const toolExternalLinks = (
    tool: ToolLike,
    language: string | undefined,
): ToolExternalLinks | null => {
    const slugs = TOOL_SITE_SLUGS[tool.slug];
    if (!slugs) return null;

    const lang = siteLanguage(language);
    return {
        moreInfo: `https://alchely.com/${lang}/marketplace/${slugs.marketplace}/`,
        documentation: `https://alchely.com/${lang}/docs/${slugs.docs}/`,
    };
};
